import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { DEFAULT_THEME_NAME, STORAGE_KEY } from "../constants.js";
import { reducer } from "./stateReducer.js";
const defaultStoredThemes = {
  activeThemeName: DEFAULT_THEME_NAME,
  activeThemePristine: true,
  themes: {
    [DEFAULT_THEME_NAME]: {}
  }
};
const StoredThemesContext = createContext(defaultStoredThemes);
function useStoredThemes() {
  return useContext(StoredThemesContext);
}
const PreviewThemeContext = createContext({
  api: {
    setPalette: () => {
    },
    setSpacing: () => {
    },
    setTheme: () => {
    },
    setTypography: () => {
    }
  },
  name: "",
  previewTheme: {},
  pristine: true
});
function usePreviewTheme() {
  return useContext(PreviewThemeContext);
}
function isThemePristine(theme) {
  const keysCount = Object.keys(theme).length;
  switch (keysCount) {
    case 0:
      return true;
    case 1: {
      const key = Object.keys(theme)[0];
      if (key === "spacing") return false;
      const changesCount = Object.keys(theme[key] ?? {}).length;
      return changesCount === 0;
    }
    default:
      return false;
  }
}
const StoredThemesApiContext = createContext({
  loadTheme: () => {
  },
  removeTheme: () => {
  },
  upSertTheme: () => {
  }
});
function useStoredThemesApi() {
  return useContext(StoredThemesApiContext);
}
function getStoredThemesFromLocalStorage() {
  const storedThemes = localStorage.getItem(STORAGE_KEY);
  return storedThemes ? JSON.parse(storedThemes) : defaultStoredThemes;
}
function StoredThemesProvider(props) {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, defaultStoredThemes, getStoredThemesFromLocalStorage);
  const { activeThemeName, themes } = state;
  const storedThemesApi = useMemo(() => {
    return {
      loadTheme: (themeName) => {
        dispatch({ themeName, type: "loadTheme" });
      },
      removeTheme: (themeName) => {
        dispatch({ themeName, type: "removeTheme" });
      },
      upSertTheme: (themeName, theme) => {
        dispatch({ theme, themeName, type: "upSertTheme" });
      }
    };
  }, []);
  const previewTheme = useMemo(() => {
    const activePalette = typeof themes[activeThemeName].colorSchemes?.light === "boolean" ? {} : themes[activeThemeName].colorSchemes?.light?.palette;
    return {
      api: {
        setPalette: (palette, mode) => {
          dispatch({
            theme: {
              ...themes[activeThemeName],
              colorSchemes: {
                ...themes[activeThemeName].colorSchemes,
                [mode]: {
                  ...activePalette,
                  palette
                }
              }
            },
            themeName: activeThemeName,
            type: "upSertTheme"
          });
        },
        setSpacing: (spacing) => {
          dispatch({
            theme: {
              ...themes[activeThemeName],
              spacing
            },
            themeName: activeThemeName,
            type: "upSertTheme"
          });
        },
        setTheme: (theme) => {
          dispatch({
            theme,
            themeName: activeThemeName,
            type: "upSertTheme"
          });
        },
        setTypography: (typography) => {
          dispatch({
            theme: {
              ...themes[activeThemeName],
              typography
            },
            themeName: activeThemeName,
            type: "upSertTheme"
          });
        }
      },
      name: activeThemeName,
      previewTheme: themes[activeThemeName],
      pristine: isThemePristine(themes[activeThemeName])
    };
  }, [activeThemeName, themes]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);
  return /* @__PURE__ */ jsx(StoredThemesContext.Provider, { value: state, children: /* @__PURE__ */ jsx(StoredThemesApiContext.Provider, { value: storedThemesApi, children: /* @__PURE__ */ jsx(PreviewThemeContext.Provider, { value: previewTheme, children }) }) });
}
export {
  StoredThemesProvider,
  usePreviewTheme,
  useStoredThemes,
  useStoredThemesApi
};
//# sourceMappingURL=StoredThemes.js.map
